import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Bell, Wallet, ShoppingBag, User, Search, Home, Users, Gift, Plus, LogOut, Shield, Package, MessagesSquare } from "lucide-react";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common";
import Footer from "@/components/Footer";
import ReferShareModal from "@/components/ReferShareModal";
import SharePujaModal from "@/components/SharePujaModal";

export const Logo = ({ tall }) => (
  <Link to="/" data-testid="brand-logo" className="flex items-center">
    <img src="/logo.png" alt="Bhakti Sakhi" className={tall ? "h-16 w-auto" : "h-12 w-auto"} />
  </Link>
);

const LINKS = [["/", "Home"], ["/community", "Community"], ["/puja-ideas", "Puja Ideas"], ["/festivals", "Festivals"], ["/shop", "Shop"], ["/refer-and-earn", "Refer & Earn"]];

function SearchBox({ className = "" }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  return (
    <form className={`relative ${className}`} onSubmit={(e) => { e.preventDefault(); if (q.trim()) nav(`/search?q=${encodeURIComponent(q.trim())}`); }}>
      <Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-[#A8A29E]" />
      <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search puja ideas, products, people…"
        className="w-full rounded-full border border-[#E7E0D3] bg-white/90 py-2 pl-10 pr-4 text-sm outline-none focus:border-[#D97706]" />
    </form>
  );
}

function IconBtn({ to, icon: I, badge, testid, label }) {
  return (
    <Link to={to} data-testid={testid} aria-label={label} className="relative rounded-full p-2 text-[#57534E] transition-colors hover:bg-[#FDF2F4] hover:text-[#6B1D2F]">
      <I className="h-5 w-5" />
      {badge > 0 && <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#D97706] px-1 text-[10px] font-bold text-white">{badge}</span>}
    </Link>
  );
}

function ProfileMenu() {
  const { user, logout } = useApp();
  if (!user) return <Link to="/login" data-testid="nav-login-btn" className="btn-maroon !px-4 !py-2">Login</Link>;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger data-testid="nav-profile-btn" className="rounded-full outline-none"><Avatar src={user.avatar} name={user.name} /></DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52 bg-white">
        <DropdownMenuItem asChild><Link to="/profile" data-testid="menu-profile"><User className="mr-2 h-4 w-4" />My Profile</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/orders" data-testid="menu-orders"><Package className="mr-2 h-4 w-4" />My Orders</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/referral-dashboard" data-testid="menu-referrals"><Gift className="mr-2 h-4 w-4" />Referral Dashboard</Link></DropdownMenuItem>
        <DropdownMenuItem asChild><Link to="/community-chat" data-testid="menu-chat"><MessagesSquare className="mr-2 h-4 w-4" />Community Chat</Link></DropdownMenuItem>
        {user.role === "admin" && <DropdownMenuItem asChild><Link to="/admin" data-testid="menu-admin"><Shield className="mr-2 h-4 w-4" />Admin Panel</Link></DropdownMenuItem>}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut className="mr-2 h-4 w-4" />Logout</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function Header() {
  const { user, cart } = useApp();
  const count = cart.reduce((s, i) => s + i.qty, 0);
  return (
    <header className="sticky top-0 z-40 border-b border-[#E7E0D3] bg-[#FFFDF7]/90 backdrop-blur-md">
      <div className="container-x flex h-16 items-center gap-4">
        <Logo />
        <nav className="ml-4 hidden items-center gap-1 lg:flex">
          {LINKS.map(([to, l]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={`nav-link-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${isActive ? "bg-[#FDF2F4] text-[#6B1D2F]" : "text-[#57534E] hover:text-[#6B1D2F]"}`}>
              {l}{l === "Refer & Earn" && <span className="ml-1 rounded-full bg-[#FEF3C7] px-1.5 text-[10px] font-bold text-[#B45309]">2%</span>}
            </NavLink>
          ))}
        </nav>
        <SearchBox className="ml-auto hidden w-56 md:block xl:w-64" />
        <div className="ml-auto flex items-center gap-0.5 md:ml-0">
          <IconBtn to="/search" icon={Search} testid="nav-search-btn" label="Search" />
          <IconBtn to="/notifications" icon={Bell} badge={user?.unread_notifications} testid="nav-notifications-btn" label="Notifications" />
          {user && <IconBtn to="/wallet" icon={Wallet} testid="nav-wallet-btn" label="Wallet" />}
          <IconBtn to="/cart" icon={ShoppingBag} badge={count} testid="nav-cart-btn" label="Cart" />
          <div className="ml-1 hidden md:block"><ProfileMenu /></div>
        </div>
      </div>
    </header>
  );
}

function MobileNav() {
  const items = [["/", Home, "Home"], ["/community", Users, "Community"], ["/shop", ShoppingBag, "Shop"], ["/refer-and-earn", Gift, "Refer"], ["/profile", User, "Profile"]];
  return (
    <nav className="fixed inset-x-0 bottom-0 z-40 border-t border-[#E7E0D3] bg-[#FFFDF7]/95 pb-[env(safe-area-inset-bottom)] backdrop-blur-md md:hidden">
      <div className="grid grid-cols-5">
        {items.map(([to, I, l]) => (
          <NavLink key={to} to={to} end={to === "/"} data-testid={`mobile-nav-${l.toLowerCase()}`}
            className={({ isActive }) => `flex flex-col items-center gap-0.5 py-2.5 text-[10px] font-semibold ${isActive ? "text-[#6B1D2F]" : "text-[#A8A29E]"}`}>
            <I className="h-5 w-5" />{l}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}

function useReferralCapture() {
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  useEffect(() => {
    const ref = params.get("ref");
    if (!ref) return;
    let visitor = localStorage.getItem("bs_visitor");
    if (!visitor) { visitor = crypto.randomUUID(); localStorage.setItem("bs_visitor", visitor); }
    const slug = pathname.startsWith("/product/") ? pathname.split("/")[2] : null;
    api.post("/referrals/click", { code: ref, product_slug: slug, visitor }).then((r) => {
      if (r.data.valid) localStorage.setItem("bs_ref", JSON.stringify({ code: ref, at: Date.now() }));
    }).catch(() => {});
  }, [params, pathname]);
}

export default function Layout({ children }) {
  const { setSharePuja, requireAuth } = useApp();
  const { pathname } = useLocation();
  useReferralCapture();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  const hideFab = ["/community-chat", "/checkout", "/cart", "/login", "/onboarding"].some((p) => pathname.startsWith(p)) || pathname.startsWith("/admin");
  return (
    <div className="min-h-screen pb-16 md:pb-0">
      <Header />
      <main>{children}</main>
      {!pathname.startsWith("/admin") && !pathname.startsWith("/community-chat") && <Footer />}
      <MobileNav />
      {!hideFab && (
        <button data-testid="mobile-share-puja-btn" onClick={requireAuth(() => setSharePuja(true))}
          className="btn-maroon fixed bottom-20 right-4 z-40 !px-4 !py-3 md:bottom-8 md:right-8">
          <Plus className="h-4 w-4" /> Share Puja
        </button>
      )}
      <ReferShareModal />
      <SharePujaModal />
    </div>
  );
}
